/**
 * Top Sessions by Token Usage Component
 * Displays a table/list of sessions with highest token consumption
 */
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, TrendingUp, ExternalLink, Folder, Calendar, ArrowDownToLine, ArrowUpFromLine, DollarSign } from 'lucide-react'
import { formatCompactNumber, getTokenThresholdColor, cn } from '@/lib/utils'
import type { TopSessionTokenUsage } from '@/types/chatbot'

export interface TopSessionsByTokensProps {
  /** List of sessions sorted by token usage */
  sessions: TopSessionTokenUsage[]
  /** Whether data is loading */
  isLoading?: boolean
  /** Error message if fetch failed */
  error?: string
  /** Card title */
  title?: string
  /** Maximum number of sessions to display */
  limit?: number
}

/**
 * Truncate text with ellipsis
 */
function truncate(text: string | undefined, maxLength: number): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/**
 * Get top model name from breakdown
 */
function getTopModel(breakdown: TopSessionTokenUsage['modelBreakdown']): string {
  if (!breakdown || breakdown.length === 0) return 'N/A'
  // Sort by total tokens to get the top model
  const sorted = [...breakdown].sort((a, b) => 
    (b.inputTokens + b.outputTokens) - (a.inputTokens + a.outputTokens)
  )
  return sorted[0].modelName
}

/**
 * Calculate input/output totals from breakdown
 */
function calculateTotals(breakdown: TopSessionTokenUsage['modelBreakdown']): { input: number; output: number } {
  if (!breakdown || breakdown.length === 0) return { input: 0, output: 0 }
  return breakdown.reduce(
    (acc, m) => ({ input: acc.input + m.inputTokens, output: acc.output + m.outputTokens }),
    { input: 0, output: 0 }
  )
}

/**
 * Top Sessions by Token Usage - Table showing sessions with highest consumption
 */
export function TopSessionsByTokens({
  sessions,
  isLoading = false,
  error,
  title = 'Top Sessions by Token Usage',
  limit = 10
}: TopSessionsByTokensProps) {
  const displaySessions = sessions.slice(0, limit)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-8" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center text-muted-foreground py-8">
            <AlertCircle className="h-12 w-12 mb-2 text-destructive" />
            <p>Failed to load top sessions</p>
            <p className="text-sm">{error}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!sessions.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center text-muted-foreground py-8">
            <TrendingUp className="h-12 w-12 mb-2 opacity-50" />
            <p>No sessions with token data</p>
            <p className="text-sm">Run some sessions to start tracking</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {/* Header */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground font-medium px-2 pb-2 border-b">
            <span className="w-8 text-center">#</span>
            <span className="flex-1">Session</span>
            <span className="w-24 text-right">Model</span>
            <span className="w-20 text-right flex items-center justify-end gap-1">
              <ArrowDownToLine className="h-3 w-3" /> In
            </span>
            <span className="w-20 text-right flex items-center justify-end gap-1">
              <ArrowUpFromLine className="h-3 w-3" /> Out
            </span>
            <span className="w-24 text-right">Total</span>
            <span className="w-20 text-right flex items-center justify-end gap-1">
              <DollarSign className="h-3 w-3" /> Est Cost
            </span>
          </div>

          {/* Rows */}
          {displaySessions.map((session, index) => {
            const totals = calculateTotals(session.modelBreakdown)
            const topModel = getTopModel(session.modelBreakdown)
            
            return (
              <div 
                key={session.sessionId}
                className="flex items-center gap-4 px-2 py-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                {/* Rank */}
                <span className="w-8 text-center text-muted-foreground font-mono text-sm">
                  {index + 1}
                </span>

                {/* Session info */}
                <div className="flex-1 min-w-0">
                  <Link 
                    to={`/sessions/${session.sessionId}`}
                    className="text-sm font-medium hover:underline flex items-center gap-1"
                  >
                    {truncate(session.summary || session.lastUserMessage || session.sessionId, 50)}
                    <ExternalLink className="h-3 w-3 opacity-50" />
                  </Link>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    {session.projectName && (
                      <span className="flex items-center gap-1">
                        <Folder className="h-3 w-3" />
                        {session.projectName}
                      </span>
                    )}
                    {session.createdAt && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(session.createdAt)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Top Model */}
                <span className="w-24 text-right font-mono text-xs text-purple-600 truncate" title={topModel}>
                  {truncate(topModel, 12)}
                </span>

                {/* Input Tokens */}
                <span className="w-20 text-right font-mono text-sm text-blue-600">
                  {formatCompactNumber(totals.input)}
                </span>

                {/* Output Tokens */}
                <span className="w-20 text-right font-mono text-sm text-green-600">
                  {formatCompactNumber(totals.output)}
                </span>

                {/* Total */}
                <span className={cn(
                  'w-24 text-right font-mono text-sm font-medium',
                  getTokenThresholdColor(session.totalTokens)
                )}>
                  {formatCompactNumber(session.totalTokens)}
                </span>

                {/* Est Cost */}
                <span className="w-20 text-right font-mono text-sm text-amber-600">
                  {session.estimatedCost != null ? `$${session.estimatedCost.toFixed(2)}` : '-'}
                </span>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
